import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LogOut,
  Settings,
  ChevronRight,
  ChevronDown,
  Palette,
  BarChart3,
  Shield,
  Bell,
  HelpCircle,
  Moon,
  Sun,
  User,
  Mail,
  Ruler,
  Star,
  TrendingUp,
  ShoppingBag,
  Shirt,
  Eye,
  EyeOff,
  Info,
  X,
  Check,
  ExternalLink,
  Globe,
  Lock,
  Smartphone,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/stores/authStore';
import { useWardrobeStore } from '@/stores/wardrobeStore';
import { setTheme } from '@/utils/theme';
import type { ThemeMode } from '@/utils/theme';
import type { Garment } from '@/types';

// ---------------------------------------------------------------------------
// Persistence keys
// ---------------------------------------------------------------------------

const MEASUREMENTS_KEY = 'vestir_measurements';
const APPEARANCE_KEY = 'vestir_appearance';
const NOTIFICATIONS_KEY = 'vestir_notifications';

// ---------------------------------------------------------------------------
// Wardrobe insight helpers
// ---------------------------------------------------------------------------

interface ColorCount {
  name: string;
  hex: string;
  count: number;
}

interface CategoryCount {
  name: string;
  count: number;
  pct: number;
}

function analyzeColors(garments: Garment[]): ColorCount[] {
  const colorMap: Record<string, { hex: string; count: number }> = {};

  const COLOR_HEX: Record<string, string> = {
    black: '#1a1a1a', white: '#f5f5f5', red: '#e53e3e', blue: '#3b82f6',
    navy: '#1e3a5f', green: '#38a169', yellow: '#ecc94b', pink: '#ed64a6',
    purple: '#805ad5', orange: '#ed8936', brown: '#8b6f47', grey: '#9ca3af',
    gray: '#9ca3af', beige: '#d4c5a9', cream: '#fdf6e3', teal: '#319795',
    maroon: '#7b2d3b', olive: '#6b7a3d', coral: '#f56565', khaki: '#bdb76b',
    burgundy: '#800020', tan: '#d2b48c', ivory: '#fffff0', lavender: '#b794f4',
    mint: '#48bb78', peach: '#fbb6ad', gold: '#d69e2e', silver: '#a0aec0',
    denim: '#4a6fa5', charcoal: '#4a5568', multicolor: '#ff69b4',
  };

  for (const g of garments) {
    for (const c of g.colors || []) {
      const name = (typeof c === 'string' ? c : c.name || '').toLowerCase().trim();
      if (!name) continue;
      if (!colorMap[name]) {
        colorMap[name] = { hex: COLOR_HEX[name] || '#9ca3af', count: 0 };
      }
      colorMap[name].count++;
    }
  }

  return Object.entries(colorMap)
    .map(([name, { hex, count }]) => ({ name, hex, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
}

function analyzeCategories(garments: Garment[]): CategoryCount[] {
  const catMap: Record<string, number> = {};
  for (const g of garments) {
    const cat = g.category || 'other';
    catMap[cat] = (catMap[cat] || 0) + 1;
  }
  const total = garments.length || 1;
  return Object.entries(catMap)
    .map(([name, count]) => ({ name, count, pct: Math.round((count / total) * 100) }))
    .sort((a, b) => b.count - a.count);
}

function computeInsights(garments: Garment[]) {
  const totalItems = garments.length;
  const totalWorn = garments.filter((g) => g.timesWorn > 0).length;
  const avgWears = totalItems > 0
    ? Math.round(garments.reduce((s, g) => s + g.timesWorn, 0) / totalItems)
    : 0;
  const favorites = garments.filter((g) => g.isFavorite).length;
  const neverWorn = garments.filter((g) => g.timesWorn === 0).length;
  const totalValue = garments.reduce((s, g) => s + (g.purchasePrice || 0), 0);

  // Most worn
  const mostWorn = [...garments].sort((a, b) => b.timesWorn - a.timesWorn).slice(0, 3);

  return { totalItems, totalWorn, avgWears, favorites, neverWorn, totalValue, mostWorn };
}

// ---------------------------------------------------------------------------
// Settings menu item
// ---------------------------------------------------------------------------

function SettingsItem({
  icon: Icon,
  label,
  subtitle,
  onClick,
  danger,
  trailing,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  subtitle?: string;
  onClick?: () => void;
  danger?: boolean;
  trailing?: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 active:bg-gray-100 transition-colors"
    >
      <div
        className={`w-9 h-9 rounded-xl flex items-center justify-center ${
          danger ? 'bg-red-50' : 'bg-[#6C3CE1]/5'
        }`}
      >
        <Icon
          size={18}
          className={danger ? 'text-red-500' : 'text-[#6C3CE1]'}
        />
      </div>
      <div className="flex-1 text-left">
        <p className={`text-sm font-medium ${danger ? 'text-red-600' : 'text-gray-900'}`}>
          {label}
        </p>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {trailing || (
        <ChevronRight size={16} className="text-gray-300" />
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Insight card
// ---------------------------------------------------------------------------

function InsightCard({
  icon: Icon,
  label,
  value,
  sublabel,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  value: string | number;
  sublabel?: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-3 flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-[#6C3CE1]/5 flex items-center justify-center">
        <Icon size={18} className="text-[#6C3CE1]" />
      </div>
      <div>
        <p className="text-lg font-bold text-gray-900">{value}</p>
        <p className="text-[11px] text-gray-400">{label}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ProfilePage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const { garments, fetchGarments } = useWardrobeStore();
  const [activeSheet, setActiveSheet] = useState<
    null | 'editProfile' | 'measurements' | 'privacy' | 'appearance' | 'notifications' | 'about' | 'faq'
  >(null);

  useEffect(() => {
    fetchGarments(true);
  }, []);

  const colors = useMemo(() => analyzeColors(garments), [garments]);
  const categories = useMemo(() => analyzeCategories(garments), [garments]);
  const insights = useMemo(() => computeInsights(garments), [garments]);

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Logged out');
  };

  return (
    <div className="flex flex-col min-h-full pb-[var(--nav-height)] bg-[#FAFAF8]">
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="bg-white border-b">
        <div className="px-4 pt-4 pb-4">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div className="relative">
              <div className="w-18 h-18 rounded-full bg-gradient-to-br from-[#6C3CE1] to-[#a78bfa] p-[2px]">
                <div className="w-full h-full rounded-full bg-white p-[2px]">
                  {user?.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt={user.displayName}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full rounded-full bg-[#6C3CE1]/10 flex items-center justify-center">
                      <User size={28} className="text-[#6C3CE1]" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h1
                className="text-xl font-bold text-gray-900 truncate"
                style={{ fontFamily: 'Playfair Display' }}
              >
                {user?.displayName || 'Vestir User'}
              </h1>
              <p className="text-sm text-gray-400 truncate">{user?.email}</p>
              {user?.bio && (
                <p className="text-sm text-gray-600 mt-1 line-clamp-2">{user.bio}</p>
              )}
            </div>
          </div>

          {/* Stats row */}
          <div className="flex items-center justify-around mt-4 pt-3 border-t border-gray-100">
            <div className="text-center">
              <p className="text-lg font-bold text-gray-900">
                {user?.stats?.garmentCount ?? insights.totalItems}
              </p>
              <p className="text-[10px] text-gray-400 font-medium">Items</p>
            </div>
            <div className="w-px h-8 bg-gray-100" />
            <div className="text-center">
              <p className="text-lg font-bold text-gray-900">
                {user?.stats?.outfitCount ?? 0}
              </p>
              <p className="text-[10px] text-gray-400 font-medium">Outfits</p>
            </div>
            <div className="w-px h-8 bg-gray-100" />
            <div className="text-center">
              <p className="text-lg font-bold text-gray-900">
                {user?.stats?.followersCount ?? 0}
              </p>
              <p className="text-[10px] text-gray-400 font-medium">Followers</p>
            </div>
            <div className="w-px h-8 bg-gray-100" />
            <div className="text-center">
              <p className="text-lg font-bold text-gray-900">
                {user?.stats?.followingCount ?? 0}
              </p>
              <p className="text-[10px] text-gray-400 font-medium">Following</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Wardrobe Insights ──────────────────────────────────── */}
      <div className="px-4 pt-4">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Wardrobe Insights</h2>

        {/* Stat cards */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <InsightCard icon={ShoppingBag} label="Total Items" value={insights.totalItems} />
          <InsightCard icon={TrendingUp} label="Avg. Wears" value={insights.avgWears} />
          <InsightCard icon={Star} label="Favorites" value={insights.favorites} />
          <InsightCard icon={Eye} label="Never Worn" value={insights.neverWorn} />
        </div>

        {/* Color Palette */}
        {colors.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-4 mb-3">
            <div className="flex items-center gap-2 mb-3">
              <Palette size={16} className="text-[#6C3CE1]" />
              <h3 className="text-sm font-semibold text-gray-700">Your Color Palette</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {colors.map((c) => (
                <div key={c.name} className="flex items-center gap-1.5 bg-gray-50 rounded-full px-2.5 py-1">
                  <div
                    className="w-4 h-4 rounded-full border border-gray-200"
                    style={{ backgroundColor: c.hex }}
                  />
                  <span className="text-xs text-gray-600 capitalize">{c.name}</span>
                  <span className="text-[10px] text-gray-400">{c.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Category Breakdown */}
        {categories.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-4 mb-3">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 size={16} className="text-[#6C3CE1]" />
              <h3 className="text-sm font-semibold text-gray-700">Category Breakdown</h3>
            </div>
            <div className="space-y-2">
              {categories.map((cat) => (
                <div key={cat.name}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600 capitalize">{cat.name}</span>
                    <span className="text-xs text-gray-400">
                      {cat.count} ({cat.pct}%)
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${cat.pct}%` }}
                      transition={{ duration: 0.6, delay: 0.1 }}
                      className="h-full bg-[#6C3CE1] rounded-full"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Total Value */}
        {insights.totalValue > 0 && (
          <div className="bg-gradient-to-r from-[#6C3CE1]/5 to-[#a78bfa]/5 rounded-xl border border-[#6C3CE1]/10 p-4 mb-4">
            <p className="text-xs text-[#6C3CE1] font-medium">Estimated Wardrobe Value</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              ${insights.totalValue.toLocaleString()}
            </p>
          </div>
        )}
      </div>

      {/* ── Settings ───────────────────────────────────────────── */}
      <div className="px-4 pt-2 pb-2">
        <h2 className="text-sm font-semibold text-gray-700 mb-2">Settings</h2>
      </div>

      <div className="bg-white border-y border-gray-100 divide-y divide-gray-50">
        <SettingsItem
          icon={User}
          label="Edit Profile"
          subtitle="Name, bio, avatar"
          onClick={() => setActiveSheet('editProfile')}
        />
        <SettingsItem
          icon={Ruler}
          label="Body Measurements"
          subtitle="Height, weight, sizes for try-on"
          onClick={() => setActiveSheet('measurements')}
        />
        <SettingsItem
          icon={Bell}
          label="Notifications"
          subtitle="Push notifications, reminders"
          onClick={() => setActiveSheet('notifications')}
        />
        <SettingsItem
          icon={Shield}
          label="Privacy"
          subtitle={user?.isPublic ? 'Public profile' : 'Private profile'}
          onClick={() => setActiveSheet('privacy')}
        />
        <SettingsItem
          icon={Moon}
          label="Appearance"
          subtitle="Theme, display preferences"
          onClick={() => setActiveSheet('appearance')}
        />
      </div>

      <div className="px-4 pt-4 pb-1">
        <h2 className="text-sm font-semibold text-gray-700 mb-2">Support</h2>
      </div>

      <div className="bg-white border-y border-gray-100 divide-y divide-gray-50">
        <SettingsItem
          icon={HelpCircle}
          label="Help & FAQ"
          subtitle="How to use Vestir"
          onClick={() => setActiveSheet('faq')}
        />
        <SettingsItem
          icon={Mail}
          label="Contact Us"
          subtitle="Report bugs, feature requests"
          onClick={() => window.open('mailto:support@vestir.app?subject=Vestir%20Feedback', '_blank')}
        />
        <SettingsItem
          icon={Info}
          label="About Vestir"
          subtitle="Version, licenses, credits"
          onClick={() => setActiveSheet('about')}
        />
      </div>

      {/* Logout */}
      <div className="px-4 pt-4 pb-6">
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
          <SettingsItem
            icon={LogOut}
            label="Log Out"
            danger
            onClick={handleLogout}
            trailing={null}
          />
        </div>

        <p className="text-[10px] text-gray-300 text-center mt-4">
          Vestir v2.0 &middot; Made with care
        </p>
      </div>

      {/* ── Sheets ────────────────────────────────────────────── */}
      <AnimatePresence>
        {activeSheet === 'editProfile' && (
          <EditProfileSheet onClose={() => setActiveSheet(null)} />
        )}
        {activeSheet === 'measurements' && (
          <MeasurementsSheet onClose={() => setActiveSheet(null)} />
        )}
        {activeSheet === 'privacy' && (
          <PrivacySheet onClose={() => setActiveSheet(null)} />
        )}
        {activeSheet === 'appearance' && (
          <AppearanceSheet onClose={() => setActiveSheet(null)} />
        )}
        {activeSheet === 'notifications' && (
          <NotificationsSheet onClose={() => setActiveSheet(null)} />
        )}
        {activeSheet === 'about' && (
          <AboutSheet onClose={() => setActiveSheet(null)} />
        )}
        {activeSheet === 'faq' && (
          <FAQSheet onClose={() => setActiveSheet(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit Profile Sheet
// ---------------------------------------------------------------------------

function EditProfileSheet({ onClose }: { onClose: () => void }) {
  const user = useAuthStore((s) => s.user);
  const updateUser = useAuthStore((s) => s.updateUser);
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      updateUser({ displayName, bio });
      toast.success('Profile updated');
      onClose();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 flex items-end"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full max-w-lg mx-auto bg-white rounded-t-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">Edit Profile</h3>
          <button onClick={onClose}>
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Avatar placeholder */}
          <div className="flex justify-center">
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-[#6C3CE1]/10 flex items-center justify-center">
                {user?.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt=""
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <User size={32} className="text-[#6C3CE1]" />
                )}
              </div>
              <button className="absolute bottom-0 right-0 w-7 h-7 bg-[#6C3CE1] rounded-full flex items-center justify-center border-2 border-white">
                <Settings size={12} className="text-white" />
              </button>
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Display Name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#6C3CE1]"
              placeholder="Your name"
            />
          </div>

          {/* Bio */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Bio</label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={3}
              maxLength={160}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:border-[#6C3CE1]"
              placeholder="Tell us about your style..."
            />
            <p className="text-[10px] text-gray-300 text-right mt-0.5">
              {bio.length}/160
            </p>
          </div>

          {/* Email (read-only) */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Email</label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full px-3 py-2.5 border border-gray-100 rounded-xl text-sm bg-gray-50 text-gray-400"
            />
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-3 bg-[#6C3CE1] text-white rounded-xl font-medium disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Reusable bottom sheet wrapper
// ---------------------------------------------------------------------------

function BottomSheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 flex items-end"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full max-w-lg mx-auto bg-white rounded-t-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <h3 className="font-semibold">{title}</h3>
          <button onClick={onClose}>
            <X size={20} className="text-gray-400" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </motion.div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Toggle row for settings
// ---------------------------------------------------------------------------

function ToggleRow({
  label,
  subtitle,
  enabled,
  onChange,
}: {
  label: string;
  subtitle?: string;
  enabled: boolean;
  onChange: (val: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <div>
        <p className="text-sm font-medium text-gray-900">{label}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      <button
        onClick={() => onChange(!enabled)}
        className={`w-11 h-6 rounded-full p-0.5 transition-colors ${
          enabled ? 'bg-[#6C3CE1]' : 'bg-gray-200'
        }`}
      >
        <motion.div
          layout
          className="w-5 h-5 bg-white rounded-full shadow-sm"
          style={{ marginLeft: enabled ? 'auto' : 0 }}
        />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Body Measurements Sheet
// ---------------------------------------------------------------------------

const MEASUREMENT_FIELDS = [
  { key: 'height', label: 'Height', unit: 'cm', placeholder: '170' },
  { key: 'weight', label: 'Weight', unit: 'kg', placeholder: '70' },
  { key: 'chest', label: 'Chest', unit: 'cm', placeholder: '95' },
  { key: 'waist', label: 'Waist', unit: 'cm', placeholder: '80' },
  { key: 'hips', label: 'Hips', unit: 'cm', placeholder: '95' },
  { key: 'inseam', label: 'Inseam', unit: 'cm', placeholder: '80' },
  { key: 'shoulderWidth', label: 'Shoulder Width', unit: 'cm', placeholder: '45' },
  { key: 'sleeveLength', label: 'Sleeve Length', unit: 'cm', placeholder: '65' },
  { key: 'neckCircumference', label: 'Neck', unit: 'cm', placeholder: '38' },
  { key: 'footLength', label: 'Foot Length', unit: 'cm', placeholder: '27' },
] as const;

function MeasurementsSheet({ onClose }: { onClose: () => void }) {
  const [values, setValues] = useState<Record<string, string>>(() => {
    try {
      return JSON.parse(localStorage.getItem(MEASUREMENTS_KEY) || '{}');
    } catch {
      return {};
    }
  });

  const [useImperial, setUseImperial] = useState(false);

  const handleSave = () => {
    localStorage.setItem(MEASUREMENTS_KEY, JSON.stringify(values));
    toast.success('Measurements saved');
    onClose();
  };

  return (
    <BottomSheet title="Body Measurements" onClose={onClose}>
      <div className="p-4 space-y-3">
        {/* Unit toggle */}
        <div className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2">
          <span className="text-xs font-medium text-gray-600">Units</span>
          <div className="flex bg-white rounded-lg border p-0.5">
            <button
              onClick={() => setUseImperial(false)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                !useImperial ? 'bg-[#6C3CE1] text-white' : 'text-gray-500'
              }`}
            >
              cm / kg
            </button>
            <button
              onClick={() => setUseImperial(true)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                useImperial ? 'bg-[#6C3CE1] text-white' : 'text-gray-500'
              }`}
            >
              in / lb
            </button>
          </div>
        </div>

        {/* Measurement fields */}
        <div className="grid grid-cols-2 gap-3">
          {MEASUREMENT_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="text-[11px] font-medium text-gray-500 mb-1 block">
                {f.label} ({useImperial ? (f.unit === 'kg' ? 'lb' : 'in') : f.unit})
              </label>
              <input
                type="number"
                inputMode="decimal"
                value={values[f.key] || ''}
                onChange={(e) => setValues((p) => ({ ...p, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#6C3CE1]"
              />
            </div>
          ))}
        </div>

        <p className="text-[10px] text-gray-300 text-center">
          Measurements are stored locally on your device
        </p>

        <button
          onClick={handleSave}
          className="w-full py-3 bg-[#6C3CE1] text-white rounded-xl font-medium"
        >
          Save Measurements
        </button>
      </div>
    </BottomSheet>
  );
}

// ---------------------------------------------------------------------------
// Privacy Sheet
// ---------------------------------------------------------------------------

function PrivacySheet({ onClose }: { onClose: () => void }) {
  const user = useAuthStore((s) => s.user);
  const updateUser = useAuthStore((s) => s.updateUser);
  const [isPublic, setIsPublic] = useState(user?.isPublic ?? true);

  const handleToggle = (val: boolean) => {
    setIsPublic(val);
    updateUser({ isPublic: val });
    toast.success(val ? 'Profile is now public' : 'Profile is now private');
  };

  return (
    <BottomSheet title="Privacy" onClose={onClose}>
      <div className="divide-y divide-gray-50">
        <ToggleRow
          label="Public Profile"
          subtitle="Others can see your outfits and follow you"
          enabled={isPublic}
          onChange={handleToggle}
        />
        <ToggleRow
          label="Show in Discover"
          subtitle="Your outfits appear in the Discover feed"
          enabled={isPublic}
          onChange={handleToggle}
        />

        <div className="px-4 py-4">
          <div className="bg-gray-50 rounded-xl p-3">
            <div className="flex items-start gap-2">
              {isPublic ? (
                <Globe size={16} className="text-[#6C3CE1] mt-0.5" />
              ) : (
                <Lock size={16} className="text-gray-400 mt-0.5" />
              )}
              <div>
                <p className="text-xs font-medium text-gray-700">
                  {isPublic ? 'Your profile is visible to everyone' : 'Your profile is private'}
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  {isPublic
                    ? 'Anyone can view your outfits, follow you, and see your wardrobe stats.'
                    : 'Only you can see your wardrobe. Others cannot follow you or see your outfits.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 py-3">
          <p className="text-xs font-medium text-gray-700 mb-2">Your Data</p>
          <p className="text-[11px] text-gray-400">
            Garment images and body photos are stored locally on your device.
            Only outfit posts you explicitly share are visible to others.
          </p>
        </div>
      </div>
    </BottomSheet>
  );
}

// ---------------------------------------------------------------------------
// Appearance Sheet
// ---------------------------------------------------------------------------

function AppearanceSheet({ onClose }: { onClose: () => void }) {
  const [settings, setSettings] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(APPEARANCE_KEY) || '{}');
    } catch {
      return {};
    }
  });

  const theme = settings.theme || 'light';
  const compactMode = settings.compactMode ?? false;

  const updateSetting = (key: string, value: any) => {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    localStorage.setItem(APPEARANCE_KEY, JSON.stringify(updated));

    if (key === 'theme') {
      setTheme(value as ThemeMode);
      const labels: Record<string, string> = { dark: 'Dark mode', light: 'Light mode', system: 'System theme' };
      toast.success(`${labels[value] || value} enabled`);
    }
  };

  return (
    <BottomSheet title="Appearance" onClose={onClose}>
      <div className="p-4 space-y-4">
        {/* Theme selector */}
        <div>
          <p className="text-xs font-medium text-gray-500 mb-2">Theme</p>
          <div className="grid grid-cols-3 gap-2">
            {[
              { key: 'light', label: 'Light', icon: Sun },
              { key: 'dark', label: 'Dark', icon: Moon },
              { key: 'system', label: 'System', icon: Smartphone },
            ].map((opt) => {
              const Icon = opt.icon;
              const isActive = theme === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => updateSetting('theme', opt.key)}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                    isActive
                      ? 'border-[#6C3CE1] bg-[#6C3CE1]/5'
                      : 'border-gray-100 hover:border-gray-200'
                  }`}
                >
                  <Icon size={20} className={isActive ? 'text-[#6C3CE1]' : 'text-gray-400'} />
                  <span className={`text-xs font-medium ${isActive ? 'text-[#6C3CE1]' : 'text-gray-500'}`}>
                    {opt.label}
                  </span>
                  {isActive && (
                    <div className="w-4 h-4 bg-[#6C3CE1] rounded-full flex items-center justify-center">
                      <Check size={10} className="text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="border-t pt-3">
          <ToggleRow
            label="Compact Closet Grid"
            subtitle="Show more items per row in the closet"
            enabled={compactMode}
            onChange={(v) => updateSetting('compactMode', v)}
          />
        </div>
      </div>
    </BottomSheet>
  );
}

// ---------------------------------------------------------------------------
// Notifications Sheet
// ---------------------------------------------------------------------------

function NotificationsSheet({ onClose }: { onClose: () => void }) {
  const [settings, setSettings] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(NOTIFICATIONS_KEY) || '{}');
    } catch {
      return {};
    }
  });

  const update = (key: string, value: boolean) => {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(updated));
  };

  return (
    <BottomSheet title="Notifications" onClose={onClose}>
      <div className="divide-y divide-gray-50">
        <ToggleRow
          label="Daily Outfit Reminder"
          subtitle="Get reminded to plan your outfit each morning"
          enabled={settings.dailyReminder ?? false}
          onChange={(v) => update('dailyReminder', v)}
        />
        <ToggleRow
          label="Unworn Item Alerts"
          subtitle="Notify when items haven't been worn in 30+ days"
          enabled={settings.unwornAlerts ?? true}
          onChange={(v) => update('unwornAlerts', v)}
        />
        <ToggleRow
          label="Social Activity"
          subtitle="New followers, likes, and comments"
          enabled={settings.socialActivity ?? true}
          onChange={(v) => update('socialActivity', v)}
        />
        <ToggleRow
          label="Weather-Based Suggestions"
          subtitle="Outfit suggestions based on today's weather"
          enabled={settings.weatherSuggestions ?? false}
          onChange={(v) => update('weatherSuggestions', v)}
        />
        <ToggleRow
          label="Try-On Results"
          subtitle="Notify when virtual try-on is ready"
          enabled={settings.tryonResults ?? true}
          onChange={(v) => update('tryonResults', v)}
        />
      </div>
    </BottomSheet>
  );
}

// ---------------------------------------------------------------------------
// About Sheet
// ---------------------------------------------------------------------------

function AboutSheet({ onClose }: { onClose: () => void }) {
  return (
    <BottomSheet title="About Vestir" onClose={onClose}>
      <div className="p-4 space-y-4">
        {/* Logo & version */}
        <div className="text-center py-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#6C3CE1] to-[#a78bfa] flex items-center justify-center mx-auto mb-3">
            <Shirt size={28} className="text-white" />
          </div>
          <h3 className="text-lg font-bold" style={{ fontFamily: 'Playfair Display' }}>Vestir</h3>
          <p className="text-xs text-gray-400 mt-1">Your virtual wardrobe, reimagined</p>
          <p className="text-[11px] text-gray-300 mt-2">Version 2.0.0</p>
        </div>

        {/* Info rows */}
        <div className="bg-gray-50 rounded-xl divide-y divide-gray-100">
          <div className="flex justify-between px-4 py-3">
            <span className="text-xs text-gray-500">Platform</span>
            <span className="text-xs font-medium text-gray-700">Web (PWA)</span>
          </div>
          <div className="flex justify-between px-4 py-3">
            <span className="text-xs text-gray-500">ML Models</span>
            <span className="text-xs font-medium text-gray-700">3 on-device</span>
          </div>
          <div className="flex justify-between px-4 py-3">
            <span className="text-xs text-gray-500">License</span>
            <span className="text-xs font-medium text-gray-700">Proprietary</span>
          </div>
        </div>

        {/* ML Models credits */}
        <div>
          <p className="text-xs font-medium text-gray-700 mb-2">On-Device ML Models</p>
          <div className="space-y-2">
            {[
              { name: 'Garment Segmentation', desc: 'SegFormer B0 (ONNX INT8)', url: 'https://huggingface.co/almogtavor/sam' },
              { name: 'Clothing Similarity', desc: 'MobileNetV3 (ONNX INT8)', url: 'https://huggingface.co/almogtavor/vestir-clothing-similarity' },
              { name: 'Garment Quality', desc: 'MobileNetV3 (ONNX INT8)', url: 'https://huggingface.co/almogtavor/vestir-garment-quality' },
            ].map((m) => (
              <button
                key={m.name}
                onClick={() => window.open(m.url, '_blank')}
                className="w-full text-left bg-gray-50 rounded-xl px-3 py-2.5 flex items-center justify-between hover:bg-gray-100 transition-colors"
              >
                <div>
                  <p className="text-xs font-medium text-gray-700">{m.name}</p>
                  <p className="text-[10px] text-gray-400">{m.desc}</p>
                </div>
                <ExternalLink size={12} className="text-gray-300" />
              </button>
            ))}
          </div>
        </div>

        <p className="text-[10px] text-gray-300 text-center pt-2">
          Made with care in Tel Aviv
        </p>
      </div>
    </BottomSheet>
  );
}

// ---------------------------------------------------------------------------
// FAQ Sheet
// ---------------------------------------------------------------------------

const FAQ_ITEMS = [
  {
    q: 'How do I add garments to my closet?',
    a: 'Go to the Closet tab and tap the + button. You can take a photo or upload from your gallery. Our AI will automatically detect and segment the garment.',
  },
  {
    q: 'How does virtual try-on work?',
    a: 'Upload a full-body photo in the Try On tab, then select a garment from your closet. Our AI model will generate a realistic image of you wearing that garment.',
  },
  {
    q: 'What are merge suggestions?',
    a: 'When you have visually similar garments, Vestir suggests merging them to keep your closet organized. Our ML model compares shape, pattern, and texture to find duplicates.',
  },
  {
    q: 'Are my photos private?',
    a: 'Yes! All garment images and body photos are stored locally on your device. Only outfit posts you explicitly share are visible to others.',
  },
  {
    q: 'How accurate is the garment segmentation?',
    a: 'Our SegFormer model runs entirely in your browser and achieves high accuracy on most clothing types. For best results, use photos with good lighting and a clear background.',
  },
  {
    q: 'Can I use Vestir offline?',
    a: 'Basic features like browsing your closet and viewing saved outfits work offline. Features like virtual try-on and social discovery require an internet connection.',
  },
  {
    q: 'How do I plan outfits for events?',
    a: 'Go to the Planner tab, create an event with a date and occasion, then add garments from your closet. Vestir will suggest weather-appropriate combinations.',
  },
  {
    q: 'How do I delete my account?',
    a: 'Contact us at support@vestir.app and we will process your account deletion within 48 hours. All your data will be permanently removed.',
  },
];

function FAQSheet({ onClose }: { onClose: () => void }) {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  return (
    <BottomSheet title="Help & FAQ" onClose={onClose}>
      <div className="divide-y divide-gray-50">
        {FAQ_ITEMS.map((item, i) => (
          <div key={i}>
            <button
              onClick={() => setOpenIdx(openIdx === i ? null : i)}
              className="w-full flex items-center justify-between px-4 py-3 text-left"
            >
              <span className="text-sm font-medium text-gray-800 pr-4">{item.q}</span>
              <ChevronDown
                size={16}
                className={`text-gray-400 flex-shrink-0 transition-transform ${
                  openIdx === i ? 'rotate-180' : ''
                }`}
              />
            </button>
            <AnimatePresence>
              {openIdx === i && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <p className="px-4 pb-3 text-xs text-gray-500 leading-relaxed">{item.a}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </BottomSheet>
  );
}
